<template>
  <v-container class="family-obliteration-container">
    <v-row no-gutters>
      <v-col cols="12" md="1">
        <router-link
          :to="{ name: 'OverviewPage', hash: '#remainder-of-estate' }"
          class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" md="3" class="form-blurb">
        <h1>Family Obliteration</h1>
        <p>
          This is the scenario where you and everyone you've named as a
          beneficiary pass away at the same time — or none of them outlive you
          by 30 days. If that happens and you haven't planned for it, the
          Intestate Succession Act decides who inherits, usually your closest
          blood relatives. That may not be what you want.
        </p>
        <p>Naming backup beneficiaries keeps that decision yours.</p>
      </v-col>
      <v-col cols="12" md="7" offset-md="1">
        <h2 class="title-descr">Family Obliteration</h2>

        <Checkbox
          v-model="enabled"
          :label="'Enable Family Obliteration'"
          color="primary"
        />

        <div v-if="enabled">
          <p class="explanation">
            Add the people, charities or trusts who should inherit the remainder
            of your estate in this situation. They cannot be anyone you have
            already named in your remainder of estate.
          </p>

          <AssignSpecificPercentages
            :testator="testator"
            :existing-specific-percentages="existingNominees"
            :excluded-beneficiaries="excludedBeneficiaries"
            :button-loading="buttonLoading"
            @submit="saveNominees"
          />
        </div>

        <div v-else class="skip-button">
          <GradientButton
            :title="'Next'"
            :small="true"
            :callout="'right'"
            :loading="buttonLoading"
            @gradientButtonClicked="saveDisabled"
          />
        </div>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
  </v-container>
</template>

<script>
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import {
    FAMILY_OBLITERATION_EXCLUDED_BENEFICIARIES_QUERY,
    PROCESS_FAMILY_OBLITERATION_NOMINEES_MUTATION,
  } from '../../constants/graphql/remainder-of-estates';
  import AssignSpecificPercentages from './AssignSpecificPercentages';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { userFacingGraphQLError } from '../../helpers/userFacingGraphQLError';

  export default {
    name: 'FamilyObliteration',
    components: {
      AssignSpecificPercentages,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        enabled: Boolean(
          this.testator.remainderOfEstate &&
            this.testator.remainderOfEstate.familyObliterationEnabled
        ),
        // Null until the network-only query below answers; the cached list is used meanwhile.
        freshExcludedBeneficiaries: null,
        errorMessage: null,
      };
    },
    apollo: {
      freshExcludedBeneficiaries: {
        query: FAMILY_OBLITERATION_EXCLUDED_BENEFICIARIES_QUERY,
        variables() {
          return { testatorId: this.testator.id };
        },
        fetchPolicy: 'network-only',
        update(data) {
          return data.remainderOfEstate
            ? data.remainderOfEstate.familyObliterationExcludedBeneficiaries
            : [];
        },
        skip() {
          return !this.testator.remainderOfEstate;
        },
      },
    },
    computed: {
      existingNominees() {
        return this.testator.remainderOfEstate.familyObliterationNominees || [];
      },
      excludedBeneficiaries() {
        if (this.freshExcludedBeneficiaries) {
          return this.freshExcludedBeneficiaries;
        }

        return (
          this.testator.remainderOfEstate
            .familyObliterationExcludedBeneficiaries || []
        );
      },
    },
    created() {
      if (
        !this.testator.remainderOfEstate ||
        !this.testator.remainderOfEstate.distribution
      )
        this.$router.push('/remainder-of-estate/distribution');
    },
    methods: {
      saveNominees({
        specificPercentages,
        deletedRecordIds,
        distributeEqually,
      }) {
        this.process({
          enabled: true,
          equalShares: Boolean(distributeEqually),
          nominees: specificPercentages,
          deletedRecordIds,
        });
      },
      saveDisabled() {
        this.process({
          enabled: false,
          equalShares: false,
          nominees: [],
          deletedRecordIds: this.existingNominees.map(nominee => nominee.id),
        });
      },
      process({ enabled, equalShares, nominees, deletedRecordIds }) {
        if (this.buttonLoading) return;
        this.buttonLoading = true;

        this.$apollo
          .mutate({
            mutation: PROCESS_FAMILY_OBLITERATION_NOMINEES_MUTATION,
            variables: {
              remainderOfEstateId: this.testator.remainderOfEstate.id,
              enabled,
              equalShares,
              familyObliterationNomineeInputs:
                this.nomineesForMutation(nominees),
              deletedRecordIds,
            },
            update: (
              cache,
              { data: { processFamilyObliterationNominees } }
            ) => {
              this.updateCache(cache, processFamilyObliterationNominees);
            },
          })
          .then(_response => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.errorMessage = userFacingGraphQLError(error);
            this.displayError();
            if (!this.errorMessage) throw error;
          });
      },
      nomineesForMutation(nominees) {
        return nominees.map(nominee => ({
          id: nominee.id,
          beneficiaryType: nominee.beneficiaryType,
          beneficiaryId: nominee.beneficiaryId,
          percentage: parseInt(nominee.percentage),
          remainderOfEstateId: this.testator.remainderOfEstate.id,
        }));
      },
      updateCache(cache, remainderOfEstate) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        data.testator.remainderOfEstate = remainderOfEstate;

        cache.writeQuery({ ...query, data: data });
      },
      redirectToNext() {
        this.$router.push({
          name: 'OverviewPage',
          hash: '#remainder-of-estate',
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .family-obliteration-container {
    margin-bottom: 48px;

    .title-descr {
      line-height: 28px;
      font-weight: 700;
    }

    .explanation {
      margin-top: 16px;
      margin-bottom: 8px;
    }

    .skip-button {
      margin-top: 32px;
    }
  }
</style>
